/**
 * Button utility functions and mixins for OC components
 */

import { css } from "styled-components";

import { IOcAppTheme } from "../config/ocTheme";
import { withAlpha } from "./colorUtils";

export type OverlayInteractionOpts = {
  /** If the theme is highlighted vs. dimmed (light vs dark overlay) */
  isHighlighted: (theme: IOcAppTheme) => boolean;

  /** Overlay colors */
  overlayDefault?: string;
  overlayHighlight?: string;

  /** Overlay opacity levels */
  hoverOpacity?: number;
  activeOpacity?: number;

  backgroundColor?: (theme: IOcAppTheme) => string;
};

/**
 * Creates an overlay interaction effect for buttons and interactive elements
 * Adds a pseudo-element overlay that appears on hover/focus with customizable colors
 * 
 * @param options - Configuration options for the overlay effect
 * @returns CSS styles for overlay interaction
 * 
 * @example
 * const StyledButton = styled.button`
 *   ${overlayInteraction({
 *     isHighlighted: (theme) => theme.isDark,
 *     hoverOpacity: 0.8
 *   })}
 * `;
 */
export const overlayInteraction = ({
  isHighlighted,
  overlayDefault = "rgba(0, 0, 0, 0.08)",
  overlayHighlight = "rgba(255, 255, 255, 0.10)",
  hoverOpacity = 1,
  activeOpacity,
  backgroundColor
}: OverlayInteractionOpts) => css`
  position: relative;
  isolation: isolate;

  ${backgroundColor !== undefined &&
  css`
    &:not(:disabled):hover,
    &:not(:disabled):focus {
      box-shadow: ${({theme}) => `0 0 0 3px ${withAlpha(backgroundColor(theme as IOcAppTheme), 0.5)}`};
      text-decoration: none;
    }
  `}

  /* keep content above overlay */
  & > * {
    position: relative;
    z-index: 1;
  }

  --btn-overlay: ${({ theme }) =>
    isHighlighted(theme as IOcAppTheme) ? overlayHighlight : overlayDefault};

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    opacity: 0;
    background: var(--btn-overlay);
    transition: opacity 0.15s ease;
  }

  &:not(:disabled):hover::after,
  &:not(:disabled):focus-visible::after {
    opacity: ${hoverOpacity};
  }

  ${activeOpacity !== undefined &&
  css`
    &:not(:disabled):active::after {
      opacity: ${activeOpacity};
    }
  `}

  &:disabled::after {
    opacity: 0;
  }
`;

export type DisabledBehaviorOpts = {
  disabledOpacity?: number;
  disablePointerEvents?: boolean; // default false (let native disabled handle it)
};

/**
 * Applies consistent disabled styling to buttons and interactive elements
 * Handles both native :disabled and ARIA disabled states
 * 
 * @param options - Configuration options for disabled behavior
 * @returns CSS styles for disabled state
 * 
 * @example
 * const StyledButton = styled.button`
 *   ${disabledBehavior({ disabledOpacity: 0.5 })}
 * `;
 */
export const disabledBehavior = ({
  disabledOpacity = 0.65,
  disablePointerEvents = false,
}: DisabledBehaviorOpts = {}) => css`
  cursor: pointer;

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: ${disabledOpacity};
    ${disablePointerEvents ? "pointer-events: none;" : ""}
  }
`;

